/**
 * The editor open in the canvas panel right now, if any (plan 7.4): the
 * suggestions card applies through it, so an applied suggestion shows in
 * the editor at once and is saved in its session (same base, same ids), and
 * the card can tell whether marks are drawn beside the targeted blocks.
 * One editor at a time, like the panel.
 */
import { useSyncExternalStore } from 'react';
import type { ArtifactDocument, PageEditOp } from '@tabbio/types';

export type ActiveEditor = {
  artifactId: string;
  kind: 'page' | 'document';
  /** The version the editor's next save is made against. */
  baseVersionId: () => string;
  /** Pages: mirror the ops in the frame and queue them. */
  applyPageOps?: (ops: readonly PageEditOp[]) => void;
  /** Documents: change the editor's document (saved like any change). */
  applyDocument?: (change: (document: ArtifactDocument) => ArtifactDocument) => void;
  /** The editor's document now (documents), to check a suggestion's words. */
  document?: () => ArtifactDocument | null;
  /** Scroll to and outline a suggestion's target. */
  reveal?: (target: string) => void;
};

let active: ActiveEditor | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

/** The panel registers its editor while it is in edit mode; the returned function unregisters it. */
export function registerActiveEditor(editor: ActiveEditor): () => void {
  active = editor;
  notify();
  return () => {
    if (active === editor) {
      active = null;
      notify();
    }
  };
}

export function readActiveEditor(artifactId: string | null | undefined): ActiveEditor | null {
  return active && artifactId && active.artifactId === artifactId ? active : null;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Whether the panel edits this artifact now. */
export function useIsArtifactInEditor(artifactId: string | null | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => readActiveEditor(artifactId) !== null,
    () => false,
  );
}

export function resetActiveEditorForTests() {
  active = null;
  listeners.clear();
}
